<template>
	<HiddenHorizontalScrollbar>
		<TitleDetailLink
			v-for="(season, index) of seasons"
			:key="season.id"
			:title="season"
			:fullPath="season.content.fullPath"
			class="season-card__link"
		>
			<div class="season-card" @click="trackSeasonTitleClicked(season, index)">
				<PictureComp
					v-if="season.content.posterUrl"
					class="season__image"
					:alt="season.content.title"
					imageProfile="poster"
					:imageUrl="season.content.posterUrl"
					:slug="getTitleSlug(season.content.fullPath)"
					:lazy="true"
				/>
				<div class="season-info">
					<div class="season-info__text season-number" v-if="season.content.seasonNumber">
						{{ $t('WEBAPP_SEASON') }} {{ season.content.seasonNumber }}
					</div>
					<div class="season-info__text episodes-number" v-if="season.totalEpisodeCount">
						{{ season.totalEpisodeCount }}
						{{ season.totalEpisodeCount > 1 ? $t('WEBAPP_EPISODES') : $t('WEBAPP_EPISODE') }}
					</div>
				</div>
			</div>
		</TitleDetailLink>
	</HiddenHorizontalScrollbar>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ObjectType } from '@/@types/graphql-types';

interface SeasonsListProps {
	title: TitleDetail & { __typename: 'Show' };
	additionalContexts: SnowplowContext[];
}

const props = withDefaults(defineProps<SeasonsListProps>(), {
	additionalContexts: () => [],
});

const seasons = computed(() => props.title.seasons ?? []);

function getTitleSlug(fullPath: string) {
	const slug = fullPath?.match(/([^\/]*)$/);
	return slug?.[0] ?? 'Poster';
}

function trackSeasonTitleClicked(title: (TitleDetail & { __typename: 'Show' })['seasons'][0], index: number) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'title_clicked', label: `${title.content.seasonNumber}`, property: 'season', value: index },
		[
			new SnowplowTitleContextGraphql(title?.show?.objectId, ObjectType.Show, title.content.seasonNumber),
			...props.additionalContexts,
		]
	);
}
</script>

<style lang="scss" scoped>
.season-card {
	position: relative;
	display: flex;
	flex-direction: row;
	height: 72px;
	background: #111924;
	border-radius: 8px;
	margin-inline-end: 16px;
	text-wrap: nowrap;
}
.season-number {
	margin-bottom: 8px;
	font-weight: 900;
}
.episodes-number {
	color: #fff;
}
.season-info {
	display: flex;
	flex-direction: column;
	justify-content: center;
	margin: 0 16px;
}
.season__image {
	width: 48px;
	height: 72px;
	:deep(img) {
		border-radius: 8px;
		width: 48px;
		height: 72px;
	}
}
.season-card__link {
	:deep(.season-card__link.poster-link) {
		width: unset;
	}
}
</style>
